import {
  createContext,
  useCallback,
  useMemo,
  useState,
  type PropsWithChildren,
} from "react";

import type { UploadDocumentsContextValue, UploadedFile } from "../types";

export const UploadFilesContext = createContext<
  UploadDocumentsContextValue | undefined
>(undefined);

export function UploadFilesProvider({
  children,
}: PropsWithChildren): React.JSX.Element {
  const [files, setFiles] = useState<UploadedFile[]>([]);
  const [error, setError] = useState<string | null>(null);

  const clearError = useCallback(() => setError(null), []);

  const handleUploadFiles = useCallback((acceptedFiles: File[]) => {
    const newFiles = acceptedFiles.map((file) => ({
      name: file.name,
      file,
    }));
    setFiles((prev) => [...prev, ...newFiles]);
  }, []);

  const handleDeleteFile = useCallback((file: UploadedFile) => {
    setFiles((prev) => prev.filter((f) => f.name !== file.name));
  }, []);

  const clearFiles = useCallback(() => setFiles([]), []);

  const value = useMemo<UploadDocumentsContextValue>(
    () => ({
      files,
      handleUploadFiles,
      handleDeleteFile,
      clearFiles,
      error,
      setError,
      clearError,
    }),
    [files, handleUploadFiles, handleDeleteFile, clearFiles, error, clearError],
  );

  return (
    <UploadFilesContext.Provider value={value}>
      {children}
    </UploadFilesContext.Provider>
  );
}
